import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { AppLocalStorageKeys } from '@/Infrastructure/Configuration/types';

/**
 * Columns a dispatcher hid via Show/Hide Columns, keyed by boardLayoutStorageKey.
 *
 * Holds the set of hidden fields rather than a whole GridColumnVisibilityModel: the model also carries the
 * structural entries the board owns (the selection column, the hidden companion sort columns from
 * expandSubColumns), and those have to follow the query definition rather than a value frozen at the last toggle.
 */
type HiddenColumnStore = Record<string, string[] | undefined>;

/** Visibility models are Record<string, boolean>, but an absent field is visible — these admit that gap. */
type PartialVisibility = Record<string, boolean | undefined>;

/**
 * Scopes a dispatcher's own board layout to one query on one terminal: per board query so each query keeps its
 * own layout, and per terminal because the query list is fetched per terminal and names are only unique within
 * one. Shared with the column width store (see columnWidths.ts) so the two stay in step.
 */
export function boardLayoutStorageKey(terminalNumber: string | undefined, queryName: string): string {
  return `${terminalNumber ?? ''}|${queryName}`;
}

function readStore(): HiddenColumnStore {
  const stored = localStorage.getItem(AppLocalStorageKeys.DispatchHiddenColumns);
  if (!stored) return {};

  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    return Object.entries(parsed).reduce<HiddenColumnStore>((accumulator, [key, value]) => {
      if (Array.isArray(value)) {
        accumulator[key] = value.filter((field): field is string => typeof field === 'string');
      }
      return accumulator;
    }, {});
  } catch {
    return {};
  }
}

/**
 * Layers the dispatcher's hidden columns onto the query's own visibility model. A stored field the query no
 * longer carries is harmless — the grid ignores visibility entries for columns it does not have.
 */
export function applyHiddenColumns(queryVisibilityModel: GridColumnVisibilityModel, key: string): GridColumnVisibilityModel {
  const hiddenFields = readStore()[key];
  if (!hiddenFields?.length) return queryVisibilityModel;

  return hiddenFields.reduce<GridColumnVisibilityModel>(
    (accumulator, field) => {
      accumulator[field] = false;
      return accumulator;
    },
    { ...queryVisibilityModel }
  );
}

/** Persists only what the dispatcher hid, which is everything false in the model that the query had not already hidden. */
export function persistHiddenColumns(model: GridColumnVisibilityModel, queryVisibilityModel: GridColumnVisibilityModel, key: string): void {
  const visibility: PartialVisibility = model;
  const queryVisibility: PartialVisibility = queryVisibilityModel;

  const hiddenFields = Object.keys(model).filter((field) => visibility[field] === false && queryVisibility[field] !== false);

  // JSON.stringify drops undefined values, so this clears the entry when nothing is hidden
  const store: HiddenColumnStore = { ...readStore(), [key]: hiddenFields.length ? hiddenFields : undefined };

  localStorage.setItem(AppLocalStorageKeys.DispatchHiddenColumns, JSON.stringify(store));
}
